import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, InputGroup, ProgressBar } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar, X } from "react-feather";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import { getMRGroupsMstrlst, saveMRGroupsMstrInsert, saveReceipts } from "apiServices/reducer/apiService";
import { useDispatch, useSelector } from "react-redux";
import '../../../styles/user.css';
import {CustomSuccessModal} from "components";

const UploadReciepts = ({ show, onHide, refreshReceipts, selectedFamilyMember }) => {
  const dispatch = useDispatch();
  // State variables
  const [formValues, setFormValues] = useState({
    reportName: "",
    groupName: "",
    newGroupName: "",
    providerName: "",
    hospitalName: "",
    reportDate: null,
    allowMultiple: true
  });
  
  const [files, setFiles] = useState([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [groupsLst, setGroupsLst] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [shouldUpload, setShouldUpload] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  // Validation state
  const [validationErrors, setValidationErrors] = useState({
    reportName: "",
    hospitalName: "",
    reportDate: "",
    files: ""
  });
  
  const fileInputRef = useRef(null);
  const uploadInProgressRef = useRef(false);
  const userData = useSelector((state) => state.apiService?.login);
  const token = useSelector((state) => state.apiService?.token);
  
  // Use selectedFamilyMember's user_id if available, otherwise use current user's ID
  const userId = selectedFamilyMember?.user_id || userData?.user_id;

  // Add state for drag and drop visual feedback
  const [isDragOver, setIsDragOver] = useState(false);

  // Reset form and file state
  const resetForm = () => {
    setFormValues({
      reportName: "",
      groupName: "",
      newGroupName: "",
      providerName: "",
      hospitalName: "",
      reportDate: null,
      allowMultiple: true
    });
    setFiles([]);
    setIsAddingNew(false);
    setSelectedGroup("");
    setSubmitted(false);
    setProgress(0);
    setError(null);
    setSuccessMessage("");
    setShouldUpload(false);
    setIsDragOver(false);
    setValidationErrors({
      reportName: "",
      hospitalName: "",
      reportDate: "",
      files: ""
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Handle modal close: reset form then call onHide
  const handleClose = () => {
    resetForm();
    onHide();
  };

  // Handle upload effect
  useEffect(() => {
    if (shouldUpload && !uploadInProgressRef.current) {
      handleUpload();
    }
  }, [shouldUpload]);

  // Debug effect to monitor files state changes
  useEffect(() => {
    console.log('Files state changed:', files);
    console.log('Files count:', files.length);
  }, [files]);

  // Validation function
  const validateForm = () => {
    const errors = {
      reportName: "",
      hospitalName: "",
      reportDate: "",
      files: ""
    };

    if (!formValues.reportName.trim()) {
      errors.reportName = "Receipt name is required";
    }

    if (files.length === 0) {
      errors.files = "Please select at least one document to upload";
    }

    setValidationErrors(errors);
    return Object.values(errors).every(error => error === "");
  };

  const handleUpload = async () => {
    if (uploadInProgressRef.current) return;

    // Validate form before upload
    if (!validateForm()) {
      return;
    }

    uploadInProgressRef.current = true;
    setSubmitted(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('chunk', file);
        formData.append('Index', '0');
        formData.append('TotalChunks', '1');
        formData.append('FileName', file.name);
        // If no group is selected, upload as general (GroupID = '0')
        formData.append('GroupID', selectedGroup || '0');
        formData.append('TagNo', formValues.reportName);
        formData.append('ProviderName', formValues.providerName);
        formData.append('HospitalName', formValues.hospitalName);
        formData.append('UserID', userId);
        formData.append('SiteID', '0');
        formData.append('ReportDate', formValues.reportDate ? formatDateToYYYYMMDD(formValues.reportDate) : '');
        formData.append('token', token);

        setProgress(30);

        dispatch(saveReceipts(formData)).then((res) => {
          console.log(res, "res from saveReceipts");
          if(res.payload.status === 201){
            setSubmitted(false);
            setShowSuccessModal(true);
          //  setTimeout(() => {
              handleClose();
              refreshReceipts();
         //   }, 1500);
            console.log(uploadInProgressRef.current, "uploadInProgressRef.current");
            uploadInProgressRef.current = false;
          }else{
            setError('Failed to upload receipt. Please try again.');
            setProgress(0);
            setSubmitted(false);
            uploadInProgressRef.current = false;
            setShouldUpload(false);
          }
        });
      }
    } catch (error) {
      setError(error.message || 'Failed to upload report');
      setProgress(0);
      setSubmitted(false);
      uploadInProgressRef.current = false;
      setShouldUpload(false);
    }
  };

  const onUpload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploadInProgressRef.current) {
      setShouldUpload(true);
      handleUpload();
    }
  };

  // Load groups when component mounts or when selectedFamilyMember changes
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!token || !userId) {
          throw new Error('Authentication token or user ID not found');
        }

        // Create the request body with required parameters
        const requestBody = {
          token: token,
          UserID: userId,
          SiteID: 0,
          Active: true
        };

        // Dispatch the thunk action with the request body
        const resultAction = await dispatch(getMRGroupsMstrlst(requestBody));
        
        if (getMRGroupsMstrlst.fulfilled.match(resultAction)) {
          const response = resultAction.payload;
          console.log('Raw API Response:', response); // Debug log

          // Check if response exists
          if (!response) {
            console.warn('Empty response from server');
            setError('No response received from server');
            return;
          }

          // Handle different response structures
          let groupsData;
          console.log(response, "response from getMRGroupsMstrlst");
          if (response.Table && Array.isArray(response.Table)) {
            groupsData = response.Table;
          } else if (Array.isArray(response)) {
            groupsData = response;
          } else if (response.data) {
            groupsData = response.data.Table || response.data;
          } else {
            groupsData = response;
          }

          console.log('Processed Groups Data:', groupsData); // Debug log

          if (!groupsData || !Array.isArray(groupsData)) {
            console.warn('Invalid groups data format:', groupsData);
            setError('Invalid response format from server');
            return;
          }

          // Transform the response data into the required format
          const transformedGroups = groupsData
            .filter(group => group.name !== 'All') // Filter out the "All" option
            .map((group) => ({
              id: group.id?.toString() || group.GroupID?.toString() || '0',
              name: group.name || group.GroupName || 'Unnamed Group',
              is_deletable: group.is_deletable === 1
            }));
          
          console.log('Transformed Groups:', transformedGroups); // Debug log
          
          if (transformedGroups.length > 0) {
            setGroupsLst(transformedGroups);
          } else {
            console.warn('No groups found in transformed data');
            setError('No groups found. Please try again later.');
          }
        } else {
          console.warn('API call failed:', resultAction.error);
          setError('Failed to load groups. Please try again later.');
        }
      } catch (error) {
        console.error('Error fetching groups:', error);
        setError('Failed to load groups. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [dispatch, token, userId, selectedFamilyMember]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ""
      });
    }
  };

  const handleDateChange = (date) => {
    setFormValues({
      ...formValues,
      reportDate: date
    });
    
    // Clear validation error when user selects a date
    if (validationErrors.reportDate) {
      setValidationErrors({
        ...validationErrors,
        reportDate: ""
      });
    }
  };

  const uploadFiles = (e) => {
    try {
      const selectedFiles = Array.from(e.target.files);
      console.log('Selected files from input count:', selectedFiles.length);
      console.log('Selected files from input:', selectedFiles);
      console.log('Allow multiple (input):', formValues.allowMultiple);
      console.log('Existing files count (input):', files.length);
      
      if (selectedFiles.length > 0) {
        let filesToAdd;
        
        if (formValues.allowMultiple) {
          // Add to existing files instead of replacing
          const existingFiles = [...files];
          const newFiles = [...selectedFiles];
          
          // Filter out duplicates based on file name and size
          const filteredNewFiles = newFiles.filter(newFile => 
            !existingFiles.some(existingFile => 
              existingFile.name === newFile.name && existingFile.size === newFile.size
            )
          );
          
          filesToAdd = [...existingFiles, ...filteredNewFiles];
          console.log('Adding to existing files (input). New unique files:', filteredNewFiles.length);
        } else {
          // Single file mode - replace
          filesToAdd = [selectedFiles[0]];
        }
        
        console.log('Total files to set (input):', filesToAdd.length);
        console.log('Files to add (input):', filesToAdd);
        setFiles(filesToAdd);
        
        // Auto-fill report name with appropriate name based on number of files
        let reportName = '';
        if (filesToAdd.length === 1) {
          reportName = filesToAdd[0].name.split('.').slice(0, -1).join('.');
        } else {
          reportName = `${filesToAdd.length} files selected`;
        }
        console.log('Setting report name (input) to:', reportName);
        setFormValues(prev => ({ ...prev, reportName: reportName }));
        
        console.log('File input complete');
      }
      
      // Clear validation error when user selects files
      if (validationErrors.files) {
        setValidationErrors({
          ...validationErrors,
          files: ""
        });
      }
      
      // Reset the input value to allow selecting the same files again if needed
      if (e.target) {
        e.target.value = '';
      }
    } catch (error) {
      console.error('Error in file input:', error);
    }
  };

  const removeFile = (index) => {
    console.log('Removing file at index:', index);
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    
    // Update report name based on remaining files
    let reportName = '';
    if (newFiles.length === 0) {
      reportName = "";
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else if (newFiles.length === 1) {
      reportName = newFiles[0].name.split('.').slice(0, -1).join('.');
    } else {
      reportName = `${newFiles.length} files selected`;
    }
    
    setFormValues(prev => ({ ...prev, reportName: reportName }));
    
    // Clear file validation error if there are still files
    if (newFiles.length > 0 && validationErrors.files) {
      setValidationErrors(prev => ({ ...prev, files: "" }));
    }
  };

  const removeAllFiles = () => {
    console.log('Removing all files');
    setFiles([]);
    setFormValues(prev => ({ ...prev, reportName: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    // Clear any validation errors
    setValidationErrors(prev => ({ ...prev, files: "", reportName: "" }));
  };

  const getFileNameWithoutExtension = (fileName) => {
    return fileName.split('.').slice(0, -1).join('.');
  };

  // Helper function to format date to YYYY-MM-DD without timezone issues
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toggleAddNew = () => {
    setIsAddingNew(!isAddingNew);
  };

  const onGroupChange = (value) => {
    setSelectedGroup(value);
    setFormValues({
      ...formValues,
      groupName: value
    });
  };

  const addNewGroup = async () => {
    if (formValues.newGroupName) {
      try {
        setLoading(true);
        setError(null);

        // Call the API to save the new group
        const resultAction = await dispatch(saveMRGroupsMstrInsert({
          GroupName: formValues.newGroupName,
          UserID: userId,
          token: token
        }));

        if (saveMRGroupsMstrInsert.fulfilled.match(resultAction)) {
          // After successful save, fetch the updated groups list
          const fetchResult = await dispatch(getMRGroupsMstrlst({
            token: token,
            UserID: userId,
            SiteID: 0,
            Active: true
          }));

          if (getMRGroupsMstrlst.fulfilled.match(fetchResult)) {
            const response = fetchResult.payload;
            if (response.Table && Array.isArray(response.Table)) {
              const groupsData = response.Table.filter(group => group.name !== 'All');
              const transformedGroups = groupsData.map((group) => ({
                id: group.id?.toString() || group.GroupID?.toString() || '0',
                name: group.name || group.GroupName || 'Unnamed Group',
                is_deletable: group.is_deletable === 1
              }));
              
              setGroupsLst(transformedGroups);
              setSelectedGroup(transformedGroups[transformedGroups.length - 1].id);
            }
          }

          setFormValues({
            ...formValues,
            groupName: formValues.newGroupName,
            newGroupName: ""
          });
          setIsAddingNew(false);
        } else {
          setError('Failed to save new report type. Please try again.');
        }
      } catch (error) {
        console.error('Error adding new group:', error);
        setError('Failed to save new report type. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const cancelNewGroup = () => {
    setFormValues({
      ...formValues,
      newGroupName: ""
    });
    setIsAddingNew(false);
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      size="lg" 
      centered 
      backdrop="static"
      dialogClassName="receipts-modal-size"
      keyboard={false}
      enforceFocus={false}
    >
      <Modal.Header className="custom-modal-header">
        <Modal.Title className="text-capitalize fw-bold">Upload Receipts</Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleClose}
        ></button>
      </Modal.Header>
      <Modal.Body style={{ padding: "2rem 2.5rem" }}>
        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="alert alert-success mb-3" role="alert">
            {successMessage}
          </div>
        )}
        <Form autoComplete="off">
          <Form.Group className="mb-3">
            <Form.Label className="fw-normal">Receipt Name:</Form.Label>
            {/* <InputGroup>
              <Form.Control
                type="text"
                name="reportName"
                value={formValues.reportName}
                onChange={handleInputChange}
                className={`flatpickr-input-custom ${validationErrors.reportName && formValues.reportName.trim() === "" ?'input-error' : ''}`}
                style={{ height: '50px' }}
              />
              <Button
                className={`choose-documents-btn text-capitalize ${validationErrors.files ? 'input-error' : ''}`}
                onClick={() => fileInputRef.current.click()}
                style={{ height: '50px' }}
              >
                Choose Document(s)
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                id="account-upload"
                hidden
                multiple={formValues.allowMultiple}
                onChange={uploadFiles}
              />
              {files.length > 0 && (
                <Button
                  variant="outline-danger"
                  onClick={removeAllFiles}
                  title="Remove All Files"
                  className="remove-all-files-btn ms-2"
                  style={{ height: '50px' }}
                >
                  <X size={22} color="var(--pmr-danger)" />
                </Button>
              )}
            </InputGroup> */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(true);
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Only set to false if we're leaving the drop zone entirely
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setIsDragOver(false);
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(false);
                
                try {
                  const droppedFiles = Array.from(e.dataTransfer.files);
                  console.log('Dropped files count:', droppedFiles.length);
                  console.log('Dropped files:', droppedFiles);
                  console.log('Allow multiple:', formValues.allowMultiple);
                  console.log('Existing files count:', files.length);
                  
                  if (droppedFiles.length > 0) {
                    let filesToAdd;
                    
                    if (formValues.allowMultiple) {
                      // Add to existing files instead of replacing
                      const existingFiles = [...files];
                      const newFiles = [...droppedFiles];
                      
                      // Filter out duplicates based on file name and size
                      const filteredNewFiles = newFiles.filter(newFile => 
                        !existingFiles.some(existingFile => 
                          existingFile.name === newFile.name && existingFile.size === newFile.size
                        )
                      );
                      
                      filesToAdd = [...existingFiles, ...filteredNewFiles];
                      console.log('Adding to existing files. New unique files:', filteredNewFiles.length);
                    } else {
                      // Single file mode - replace
                      filesToAdd = [droppedFiles[0]];
                    }
                    
                    console.log('Total files to set:', filesToAdd.length);
                    console.log('Files to add:', filesToAdd);
                    console.log('Setting files...');
                    
                    setFiles(filesToAdd);
                    
                    // Auto-fill report name with appropriate name based on number of files
                    let reportName = '';
                    if (filesToAdd.length === 1) {
                      reportName = filesToAdd[0].name.split('.').slice(0, -1).join('.');
                    } else {
                      reportName = `${filesToAdd.length} files selected`;
                    }
                    console.log('Setting report name to:', reportName);
                    setFormValues(prev => ({ ...prev, reportName: reportName }));
                    
                    // Clear any validation errors
                    setValidationErrors(prev => ({ ...prev, files: "", reportName: "" }));
                    
                    console.log('Drag and drop complete');
                  } else {
                    console.log('No files dropped');
                  }
                } catch (error) {
                  console.error('Error in drag and drop:', error);
                }
              }}
              className={`drag-drop-area ${isDragOver ? 'drag-over' : ''}`}
              style={{ width: '100%', marginBottom: '30px', fontWeight: 'bold'}}
            >
              <InputGroup>
                <Form.Control
                  type="text"
                  name="reportName"
                  value={formValues.reportName}
                  onChange={handleInputChange}
                  className={`flatpickr-input-custom ${validationErrors.reportName && formValues.reportName.trim() === "" ?'input-error' : ''}`}
                  style={{ height: '50px' }}
                />
                <Button
                  className={`choose-documents-btn text-capitalize ${validationErrors.files ? 'input-error' : ''}`}
                  onClick={() => fileInputRef.current.click()}
                  style={{ height: '50px' }}
                >
                  Choose Document(s)
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  id="account-upload"
                  hidden
                  multiple={formValues.allowMultiple}
                  onChange={uploadFiles}
                />
                {files.length > 0 && (
                  <Button
                    variant="outline-danger"
                    onClick={removeAllFiles}
                    title="Remove All Files"
                    className="remove-all-files-btn ms-2"
                    style={{ height: '50px' }}
                  >
                    <X size={22} color="var(--pmr-danger)" />
                  </Button>
                )}
              </InputGroup>
            </div>

          </Form.Group>
          
          {files.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>
                <span className="fw-normal">Selected Documents</span>
                <span style={{ color: "red", fontWeight: "bold" }}> ({files.length})</span> :
              </Form.Label>
              <div className="selected-documents-list-receipts">
                <ul>
                  {files.map((file, i) => (
                    <li key={i} className="d-flex align-items-center mb-1">
                      <span className="fw-bold">{i + 1}.</span>
                      <span className="fw-semibold">{getFileNameWithoutExtension(file.name)}</span>
                      <Button
                        variant="link"
                        className="text-danger p-0 remove-file-btn"
                        onClick={() => removeFile(i)}
                      >
                        <X size={16} color="var(--pmr-danger)" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            </Form.Group>
          )}
          

          

          <Form.Group className="mb-3">
            <Form.Label className="fw-normal">Clinic / Diagnostics Center Name:</Form.Label>
            <Form.Control
              type="text"
              name="hospitalName"
              value={formValues.hospitalName}
              onChange={handleInputChange}
              className={`flatpickr-input-custom ${validationErrors.hospitalName ? 'input-error' : ''}`}
              style={{ height: '50px' }}
            />

          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label className="fw-normal">Receipt Date:</Form.Label>
            <div style={{ position: "relative" }}>
              <Flatpickr
                value={formValues.reportDate}
                onChange={([date]) => handleDateChange(date)}
                options={{
                  dateFormat: "m/d/Y",
                  maxDate: new Date()
                }}
                className={`form-control flatpickr-input-custom ${validationErrors.reportDate ? 'input-error' : ''}`}
                placeholder="mm/dd/yyyy"
                style={{ height: '50px' }}
               
              />
              <span
                className="flatpickr-calendar-icon"
                onClick={() => document.querySelector(".flatpickr-input").focus()}
              >
                <Calendar size={20} />
              </span>
            </div>

          </Form.Group>
        </Form>
        
        {submitted && (
          <div className="progress-wrapper mt-3">
            Report is being uploaded. Please wait...
            <ProgressBar
              variant="primary"
              now={progress}
              striped
              animated
            />
          </div>
        )}
      </Modal.Body>
      
      {!submitted && (
        <Modal.Footer className="justify-content-center">
          <Button 
            variant="primary" 
            onClick={onUpload} 
            className="upload-btn-receipts me-2"
            disabled={uploadInProgressRef.current}
          >
            Upload
          </Button>
          <Button 
            variant="danger" 
            onClick={handleClose} 
            className="upload-btn-receipts ms-2"
          >
            Close
          </Button>
        </Modal.Footer>
      )}
      <CustomSuccessModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        title="Receipt Uploaded Successfully"
        message="Receipt uploaded successfully!"
      />
      
      <style>
        {`
          .drag-drop-area {
            transition: all 0.2s ease;
            border-radius: 4px;
            border: 2px dashed transparent;
            padding: 2px;
            position: relative;
          }
          
          .drag-drop-area.drag-over {
            background-color: #f8f9fa;
            border: 2px dashed #007bff !important;
          }
          
          .drag-drop-area .input-group {
            position: relative;
          }
          
          .drag-drop-area.drag-over::after {
            content: 'Drop files here to add to your selection';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(0, 123, 255, 0.95);
            color: white;
            padding: 15px 25px;
            border-radius: 6px;
            font-weight: bold;
            font-size: 14px;
            z-index: 1000;
            pointer-events: none;
            box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
          }
          
          .drag-drop-area:not(.drag-over)::before {
            content: 'Drag & drop files here or click "Choose Document(s)" - Multiple files will be added together';
            position: absolute;
            bottom: -25px;
            left: 0;
            right: 0;
            text-align: center;
            font-size: 12px;
            color: red;
            font-style: italic;
          }
        `}
      </style>
    </Modal>
  );
};

export default UploadReciepts;